
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Search, UserPlus, Check, X, Clock, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Interface for user profile data
 */
interface UserProfile {
  id: string;
  username: string;
  avatar_url?: string;
  points?: number;
}

/**
 * Interface for friend request data
 */
interface FriendRequest {
  id: string;
  sender_id: string;
  receiver_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
}

/**
 * FriendsSearch Component
 * 
 * Handles searching for users, sending friend requests, and managing 
 * received/sent friend requests. Provides a complete friend management interface.
 */
const FriendsSearch = () => {
  // State for search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // State for friend requests management
  const [receivedRequests, setReceivedRequests] = useState<(FriendRequest & { sender: UserProfile })[]>([]);
  const [sentRequests, setSentRequests] = useState<(FriendRequest & { receiver: UserProfile })[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  
  const { toast } = useToast();

  /**
   * Search for users based on username
   */
  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      console.log('Searching for users with query:', query);
      
      // Get current user to exclude from results
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Search for users by username (case insensitive)
      const { data: users, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .ilike('username', `%${query}%`)
        .neq('id', user.id) // Exclude current user
        .limit(10);

      if (error) {
        console.error('Error searching users:', error);
        toast({
          title: "Search error",
          description: "Could not search for users. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Get user points for each search result
      const userIds = users?.map(u => u.id) || [];
      const { data: pointsData } = await supabase
        .from('user_points')
        .select('user_id, total_points')
        .in('user_id', userIds);

      // Combine user data with points
      const usersWithPoints = users?.map(user => ({
        ...user,
        points: pointsData?.find(p => p.user_id === user.id)?.total_points || 0
      })) || [];

      setSearchResults(usersWithPoints);
      console.log('Search results:', usersWithPoints);
    } catch (error) {
      console.error('Error in searchUsers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Send a friend request to a user
   */
  const sendFriendRequest = async (receiverId: string) => {
    try {
      console.log('Sending friend request to:', receiverId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if request already exists
      const { data: existingRequest } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('sender_id', user.id)
        .eq('receiver_id', receiverId)
        .single();

      if (existingRequest) {
        toast({
          title: "Request already sent",
          description: "You've already sent a friend request to this user.",
          variant: "destructive",
        });
        return;
      }

      // Send the friend request
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          status: 'pending'
        });

      if (error) {
        console.error('Error sending friend request:', error);
        toast({
          title: "Error sending request",
          description: "Could not send friend request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Friend request sent!",
        description: "Your friend request has been sent successfully.",
      });

      // Refresh the requests list
      loadFriendRequests();
    } catch (error) {
      console.error('Error in sendFriendRequest:', error);
    }
  };

  /**
   * Load friend requests (both received and sent)
   */
  const loadFriendRequests = async () => {
    try {
      setIsLoadingRequests(true);
      console.log('Loading friend requests...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get received requests (requests sent to current user)
      const { data: receivedRequestsData, error: receivedError } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('receiver_id', user.id)
        .eq('status', 'pending');

      if (receivedError) {
        console.error('Error loading received requests:', receivedError);
        throw receivedError;
      }

      // Get sent requests (requests sent by current user)
      const { data: sentRequestsData, error: sentError } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('sender_id', user.id)
        .eq('status', 'pending');

      if (sentError) {
        console.error('Error loading sent requests:', sentError);
        throw sentError;
      }

      // Get sender profiles for received requests
      const senderIds = receivedRequestsData?.map(r => r.sender_id) || [];
      const { data: senderProfiles } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', senderIds);

      // Get receiver profiles for sent requests
      const receiverIds = sentRequestsData?.map(r => r.receiver_id) || [];
      const { data: receiverProfiles } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', receiverIds);

      // Combine requests with profile data
      const receivedWithProfiles = receivedRequestsData?.map(request => ({
        ...request,
        sender: senderProfiles?.find(p => p.id === request.sender_id) || {
          id: request.sender_id,
          username: 'Unknown User',
          avatar_url: null
        }
      })) || [];

      const sentWithProfiles = sentRequestsData?.map(request => ({
        ...request,
        receiver: receiverProfiles?.find(p => p.id === request.receiver_id) || {
          id: request.receiver_id,
          username: 'Unknown User',
          avatar_url: null
        }
      })) || [];

      setReceivedRequests(receivedWithProfiles);
      setSentRequests(sentWithProfiles);
      
      console.log('Received requests:', receivedWithProfiles);
      console.log('Sent requests:', sentWithProfiles);
    } catch (error) {
      console.error('Error in loadFriendRequests:', error);
      toast({
        title: "Error loading requests",
        description: "Could not load friend requests. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingRequests(false);
    }
  };

  /**
   * Accept a friend request
   */
  const acceptFriendRequest = async (requestId: string) => {
    try {
      console.log('Accepting friend request:', requestId);
      
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) {
        console.error('Error accepting friend request:', error);
        toast({
          title: "Error accepting request",
          description: "Could not accept friend request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Friend request accepted!",
        description: "You are now friends!",
      });

      // Refresh the requests list
      loadFriendRequests();
    } catch (error) {
      console.error('Error in acceptFriendRequest:', error);
    }
  };

  /**
   * Decline a friend request
   */
  const declineFriendRequest = async (requestId: string) => {
    try {
      console.log('Declining friend request:', requestId);
      
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'declined', updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) {
        console.error('Error declining friend request:', error);
        toast({
          title: "Error declining request",
          description: "Could not decline friend request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Friend request declined",
        description: "The friend request has been declined.",
      });

      // Refresh the requests list
      loadFriendRequests();
    } catch (error) {
      console.error('Error in declineFriendRequest:', error);
    }
  };

  // Load friend requests when component mounts
  useEffect(() => {
    loadFriendRequests();
  }, []);

  // Debounced search when query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card className="bg-background rounded-xl shadow-sm border border-gray-100">
        <CardHeader className="p-6 border-b border-gray-100">
          <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Search className="h-5 w-5" />
            Find New Friends
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Search Results */}
            <div className="space-y-2">
              {isSearching ? (
                <div className="p-4 text-center">
                  <div className="animate-pulse">Searching...</div>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-medium text-sm">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{user.username}</div>
                          <div className="text-sm text-gray-500">{user.points || 0} points</div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => sendFriendRequest(user.id)}
                        className="flex items-center gap-1"
                      >
                        <UserPlus className="h-3 w-3" />
                        Add Friend
                      </Button>
                    </div>
                  ))}
                </div>
              ) : searchQuery.trim() && !isSearching ? (
                <div className="p-4 text-center text-gray-500">
                  No users found with that username.
                </div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Friend Requests Management */}
      <Card className="bg-background rounded-xl shadow-sm border border-gray-100">
        <CardHeader className="p-6 border-b border-gray-100">
          <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Friend Requests
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-0">
          <Tabs defaultValue="received" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-none border-b">
              <TabsTrigger value="received" className="text-sm">
                Received ({receivedRequests.length})
              </TabsTrigger>
              <TabsTrigger value="sent" className="text-sm">
                Sent ({sentRequests.length})
              </TabsTrigger>
            </TabsList>
            
            {/* Received Requests Tab */}
            <TabsContent value="received" className="mt-0">
              <div className="p-6">
                {isLoadingRequests ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg"></div>
                    ))}
                  </div>
                ) : receivedRequests.length > 0 ? (
                  <div className="space-y-3">
                    {receivedRequests.map((request) => (
                      <div 
                        key={request.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-medium">
                            {request.sender.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{request.sender.username}</div>
                            <div className="text-sm text-gray-500">
                              Sent {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => acceptFriendRequest(request.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => declineFriendRequest(request.id)}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No pending friend requests</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Sent Requests Tab */}
            <TabsContent value="sent" className="mt-0">
              <div className="p-6">
                {isLoadingRequests ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg"></div>
                    ))}
                  </div>
                ) : sentRequests.length > 0 ? (
                  <div className="space-y-3">
                    {sentRequests.map((request) => (
                      <div 
                        key={request.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-medium">
                            {request.receiver.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{request.receiver.username}</div>
                            <div className="text-sm text-gray-500">
                              Sent {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <UserPlus className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No sent friend requests</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default FriendsSearch;
